import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Menu, Store, Briefcase } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { totalItems } = useCart();

  useEffect(() => {
    const controlNavbar = () => {
      if (typeof window !== 'undefined') {
        if (window.scrollY > lastScrollY && window.scrollY > 100) {
          // Scrolling down
          setIsVisible(false);
        } else {
          // Scrolling up
          setIsVisible(true);
        }
        setLastScrollY(window.scrollY);
      }
    };

    window.addEventListener('scroll', controlNavbar);
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [lastScrollY]);

  const NavLinks = ({ onClick }: { onClick?: () => void }) => (
    <>
      <Link to="/" onClick={onClick} className="text-[#003B46] hover:text-[#006C73] transition-colors font-black tracking-wide">HOME</Link>
      <Link to="/products" onClick={onClick} className="text-[#003B46] hover:text-[#006C73] transition-colors font-black tracking-wide">PRODUCTS</Link>
      <Link to="/about" onClick={onClick} className="text-[#003B46] hover:text-[#006C73] transition-colors font-black tracking-wide">OUR STORY</Link>
      <Link to="/careers" onClick={onClick} className="text-[#003B46] hover:text-[#006C73] transition-colors font-black tracking-wide">CAREERS</Link>
    </>
  );

  return (
    <header className={`sticky top-0 z-50 w-full flex flex-col shadow-sm transition-transform duration-300 ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}>
      {/* Main Navbar */}
      <nav className="bg-white border-b border-gray-100">
        <div className="container px-4 md:px-6 flex items-center h-14 md:h-16 relative">
          {/* Mobile Hamburger (Left) */}
          <div className="md:hidden absolute left-4">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="w-12 h-12 -ml-3 flex items-center justify-center text-[#003B46] hover:text-[#006C73]" aria-label="Open menu">
                  <Menu size={28} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80vw] sm:w-[350px] bg-white pt-10">
                <div className="flex flex-col gap-6 text-lg">
                  <NavLinks onClick={() => setIsOpen(false)} />
                  
                  {/* Mobile Application Links with Icons */}
                  <div className="flex flex-col gap-4 mt-4 pt-4 border-t border-gray-100">
                    <Link to="/dealer-application" onClick={() => setIsOpen(false)} className="flex items-center gap-3 text-[#003B46] hover:text-[#006C73] font-bold text-sm uppercase tracking-wide">
                      <Store size={20} className="text-[#006C73]" /> BE A DEALER
                    </Link>
                    <Link to="/sales-rep-application" onClick={() => setIsOpen(false)} className="flex items-center gap-3 text-[#003B46] hover:text-[#006C73] font-bold text-sm uppercase tracking-wide">
                      <Briefcase size={20} className="text-[#006C73]" /> SALES REP
                    </Link>
                  </div>

                  <Link to="/checkout" onClick={() => setIsOpen(false)} className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#006C73] text-white px-4 py-2.5 text-xs font-black uppercase tracking-widest shadow-md hover:bg-[#004B57] transition-all active:scale-95 w-full max-w-[220px] mx-auto min-h-[44px]">
                    <ShoppingCart size={16} strokeWidth={3} /> VIEW CART ({totalItems})
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo (Centered on mobile, Left on desktop) */}
          <Link to="/" aria-label="Daily Demand Home" className="flex items-center justify-center w-full md:w-auto md:justify-start">
            <img 
              src="/logo.webp?v=1" 
              alt="Daily Demand" 
              width="200"
              height="80"
              className="h-10 md:h-16 w-auto scale-[1.35] md:scale-[1.5] origin-center object-contain transition-transform hover:scale-[1.4] md:hover:scale-[1.55]"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10 text-sm ml-12 uppercase tracking-widest">
            <NavLinks />
          </div>

          {/* Desktop Right - Search & Cart */}
          <div className="hidden md:flex items-center gap-4 ml-auto">
            <div className="flex items-center gap-2 border-r border-gray-200 pr-4 mr-2">
              <div className="relative group">
                <Link to="/dealer-application" className="w-10 h-10 rounded-full flex items-center justify-center text-[#003B46] hover:text-[#006C73] hover:bg-gray-50 transition-colors">
                  <Store size={20} strokeWidth={2.5} />
                </Link>
                <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-[#003B46] text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-md">
                  Dealer Application
                </div>
              </div>
              <div className="relative group">
                <Link to="/sales-rep-application" className="w-10 h-10 rounded-full flex items-center justify-center text-[#003B46] hover:text-[#006C73] hover:bg-gray-50 transition-colors">
                  <Briefcase size={20} strokeWidth={2.5} />
                </Link>
                <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-[#003B46] text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-md">
                  Sales Rep Application
                </div>
              </div>
            </div>

            <Link to="/checkout" className="inline-flex items-center justify-center gap-3 rounded-full bg-[#006C73] px-8 py-3 text-white font-black text-xs uppercase tracking-widest hover:bg-[#004B57] transition-all shadow-xl shadow-[#006C73]/20 relative">
              <ShoppingCart size={16} strokeWidth={3} />
              CART
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#003B46] text-white text-[10px] rounded-full flex items-center justify-center border-2 border-white">
                  {totalItems}
                </span>
              )}
            </Link>
          </div>


          {/* Mobile Cart Icon (Right) */}
          <div className="md:hidden absolute right-4">
            <Link to="/checkout" aria-label={`Shopping Cart, ${totalItems} items`} className="w-12 h-12 -mr-3 flex items-center justify-center text-[#003B46] hover:text-[#006C73] relative">
              <ShoppingCart size={24} />
              {totalItems > 0 && (
                <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#006C73] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                  {totalItems}
                </span>
              )}
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
